import React, { useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Checkbox,
    FormControlLabel,
    Button,
    Grid,
    Chip,
    Divider,
    Alert,
    useTheme,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';

// Helper to download a file
function downloadFile(content, filename, mimeType = 'text/csv') {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}

// Convert array of objects to CSV string
function arrayToCSV(data, headers) {
    if (!data || data.length === 0) return '';
    const headerRow = headers.join(',');
    const rows = data.map(row => headers.map(h => {
        const val = row[h];
        // Handle values with commas or quotes
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
        }
        return val ?? '';
    }).join(','));
    return [headerRow, ...rows].join('\n');
}

// Export data categories
const EXPORT_CATEGORIES = [
    {
        id: 'load_profile',
        label: 'Load Profile (8760 hourly kW)',
        icon: ElectricBoltIcon,
        format: 'CSV',
        description: 'Hourly facility load data for the full year',
    },
    {
        id: 'system_dispatch',
        label: 'System Dispatch (8760 hourly)',
        icon: SolarPowerIcon,
        format: 'CSV',
        description: 'All DER flows: PV, Battery, Generator, Grid',
    },
    {
        id: 'utility_bills',
        label: 'Utility Bills (monthly comparison)',
        icon: ReceiptIcon,
        format: 'CSV',
        description: 'Monthly BAU vs Project utility costs',
    },
    {
        id: 'cash_flows',
        label: 'Cash Flow Analysis (annual proforma)',
        icon: TrendingUpIcon,
        format: 'CSV',
        description: 'Year-by-year cash flows, savings, cumulative totals',
    },
    {
        id: 'tariff',
        label: 'Tariff Structure',
        icon: ReceiptIcon,
        format: 'JSON',
        description: 'Rate periods, energy charges, demand charges',
    },
    {
        id: 'der_config',
        label: 'DER Configuration',
        icon: SettingsIcon,
        format: 'JSON',
        description: 'PV, Battery, Generator sizes and costs',
    },
    {
        id: 'raw_results',
        label: 'Full Raw Results',
        icon: DataObjectIcon,
        format: 'JSON',
        description: 'Complete optimization output JSON',
    },
];

export default function ExportDataTab({
    results,
    loadProfile,
    scenarioForm,
    scenarioName = 'scenario',
    financialAnalysis = null,  // From FinancialCalculator - budget-based financials
    budget = null,              // Budget hook data
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [selected, setSelected] = useState(new Set(['load_profile', 'system_dispatch', 'cash_flows']));
    const [exporting, setExporting] = useState(false);

    const toggleCategory = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const selectAll = () => {
        setSelected(new Set(EXPORT_CATEGORIES.map(c => c.id)));
    };

    const selectNone = () => {
        setSelected(new Set());
    };

    // Generate export data for each category
    const generateExport = (categoryId) => {
        const outputs = results?.outputs || {};
        const site = outputs.Site || {};
        const pv = outputs.PV || {};
        const storage = outputs.ElectricStorage || {};
        const financial = outputs.Financial || site.Financial || {};

        switch (categoryId) {
            case 'load_profile': {
                const loads = loadProfile?.loads || site.load_kw || [];
                if (loads.length === 0) return null;
                const data = loads.map((kw, i) => ({
                    hour: i,
                    load_kw: typeof kw === 'number' ? kw.toFixed(2) : kw,
                }));
                return {
                    content: arrayToCSV(data, ['hour', 'load_kw']),
                    filename: `${scenarioName}_load_profile.csv`,
                    mimeType: 'text/csv',
                };
            }

            case 'system_dispatch': {
                // Comprehensive system dispatch including all DER assets
                const loads = loadProfile?.loads || site.load_kw || [];
                const pvToLoad = pv.electric_to_load_series_kw || [];
                const pvToGrid = pv.electric_to_grid_series_kw || [];
                const pvToStorage = storage.pv_to_storage_series_kw || [];
                const soc = storage.soc_series_fraction || [];
                const batteryToLoad = storage.electric_to_load_series_kw || [];
                const batteryToGrid = storage.electric_to_grid_series_kw || [];
                const gridToStorage = storage.grid_to_storage_series_kw || [];
                const gridToLoad = site.electric_from_grid_series_kw || [];
                const generator = outputs.Generator || {};
                const genToLoad = generator.electric_to_load_series_kw || [];

                // Must have at least load or PV data
                if (loads.length === 0 && pvToLoad.length === 0) return null;
                const numHours = Math.max(loads.length, pvToLoad.length, soc.length, 8760);

                const data = [];
                for (let i = 0; i < numHours; i++) {
                    data.push({
                        hour: i,
                        load_kw: (loads[i] || 0).toFixed(2),
                        pv_to_load_kw: (pvToLoad[i] || 0).toFixed(2),
                        pv_to_grid_kw: (pvToGrid[i] || 0).toFixed(2),
                        pv_to_storage_kw: (pvToStorage[i] || 0).toFixed(2),
                        battery_soc_pct: ((soc[i] || 0) * 100).toFixed(1),
                        battery_to_load_kw: (batteryToLoad[i] || 0).toFixed(2),
                        battery_to_grid_kw: (batteryToGrid[i] || 0).toFixed(2),
                        grid_to_storage_kw: (gridToStorage[i] || 0).toFixed(2),
                        grid_to_load_kw: (gridToLoad[i] || 0).toFixed(2),
                        generator_to_load_kw: (genToLoad[i] || 0).toFixed(2),
                    });
                }

                const headers = ['hour', 'load_kw', 'pv_to_load_kw', 'pv_to_grid_kw', 'pv_to_storage_kw', 'battery_soc_pct', 'battery_to_load_kw', 'battery_to_grid_kw', 'grid_to_storage_kw', 'grid_to_load_kw', 'generator_to_load_kw'];
                return {
                    content: arrayToCSV(data, headers),
                    filename: `${scenarioName}_system_dispatch.csv`,
                    mimeType: 'text/csv',
                };
            }

            case 'utility_bills': {
                const bauBills = financial.bau_monthly_utility_bills || [];
                const projectBills = financial.monthly_utility_bills || [];
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                if (bauBills.length === 0 && projectBills.length === 0) return null;
                const data = months.map((m, i) => ({
                    month: m,
                    bau_cost: (bauBills[i] || 0).toFixed(2),
                    project_cost: (projectBills[i] || 0).toFixed(2),
                    savings: ((bauBills[i] || 0) - (projectBills[i] || 0)).toFixed(2),
                }));
                return {
                    content: arrayToCSV(data, ['month', 'bau_cost', 'project_cost', 'savings']),
                    filename: `${scenarioName}_utility_bills.csv`,
                    mimeType: 'text/csv',
                };
            }

            case 'cash_flows': {
                // PRIORITY: Use budget-based financial analysis cash flows
                // This ensures exported data matches what's displayed in the UI
                if (financialAnalysis?.projectCashflows?.length > 0) {
                    const initial = financialAnalysis.summary?.initialInvestment || 0;
                    const data = [];
                    let cumulative = -initial;

                    // Year 0 - initial investment
                    data.push({
                        year: 0,
                        initial_investment: (-initial).toFixed(2),
                        energy_savings: '0',
                        om_costs: '0',
                        fuel_costs: '0',
                        der_charges: '0',
                        net_cash_flow: (-initial).toFixed(2),
                        cumulative: cumulative.toFixed(2),
                    });

                    // Years 1-N from budget-based analysis
                    financialAnalysis.projectCashflows.forEach(cf => {
                        cumulative += (cf.netCashflow || 0);
                        data.push({
                            year: cf.year,
                            initial_investment: '0',
                            energy_savings: (cf.energySavings || 0).toFixed(2),
                            om_costs: (cf.omCost || 0).toFixed(2),
                            fuel_costs: (cf.fuelCost || 0).toFixed(2),
                            der_charges: (cf.derCharges || 0).toFixed(2),
                            net_cash_flow: (cf.netCashflow || 0).toFixed(2),
                            cumulative: cumulative.toFixed(2),
                        });
                    });

                    return {
                        content: arrayToCSV(data, ['year', 'initial_investment', 'energy_savings', 'om_costs', 'fuel_costs', 'der_charges', 'net_cash_flow', 'cumulative']),
                        filename: `${scenarioName}_cash_flows.csv`,
                        mimeType: 'text/csv',
                    };
                }

                // FALLBACK: Use saved lifecycle data if financialAnalysis not available
                const lifecycle = financial.lifecycle_cashflow || financial.annual_cashflows || [];
                if (lifecycle.length === 0) {
                    // No budget-based analysis and no saved lifecycle - return warning message
                    // DO NOT use solver financial.initial_capital_costs
                    return {
                        content: 'Cash flow data not available. Please ensure the scenario has been saved after running optimization with the Budget tab configured.',
                        filename: `${scenarioName}_cash_flows_unavailable.txt`,
                        mimeType: 'text/plain',
                    };
                }
                return {
                    content: arrayToCSV(lifecycle, Object.keys(lifecycle[0] || {})),
                    filename: `${scenarioName}_cash_flows.csv`,
                    mimeType: 'text/csv',
                };
            }

            case 'tariff': {
                const tariff = scenarioForm?.selectedTariffData || results?.inputs?.ElectricTariff || {};
                return {
                    content: JSON.stringify(tariff, null, 2),
                    filename: `${scenarioName}_tariff.json`,
                    mimeType: 'application/json',
                };
            }

            case 'der_config': {
                const config = {
                    pv: {
                        size_kw: pv.size_kw || scenarioForm?.pvMaxKw || 0,
                        cost_per_kw: scenarioForm?.pvCost || 0,
                    },
                    battery: {
                        power_kw: storage.size_kw || scenarioForm?.storageMaxKw || 0,
                        capacity_kwh: storage.size_kwh || scenarioForm?.storageMaxKwh || 0,
                    },
                    generator: {
                        size_kw: outputs.Generator?.size_kw || scenarioForm?.generatorMaxKw || 0,
                    },
                };
                return {
                    content: JSON.stringify(config, null, 2),
                    filename: `${scenarioName}_der_config.json`,
                    mimeType: 'application/json',
                };
            }

            case 'raw_results': {
                return {
                    content: JSON.stringify(results, null, 2),
                    filename: `${scenarioName}_raw_results.json`,
                    mimeType: 'application/json',
                };
            }

            default:
                return null;
        }
    };

    const handleDownloadSelected = () => {
        setExporting(true);
        try {
            selected.forEach(id => {
                const exportData = generateExport(id);
                if (exportData) {
                    downloadFile(exportData.content, exportData.filename, exportData.mimeType);
                }
            });
        } finally {
            setExporting(false);
        }
    };

    const handleDownloadSingle = (id) => {
        const exportData = generateExport(id);
        if (exportData) {
            downloadFile(exportData.content, exportData.filename, exportData.mimeType);
        }
    };

    // Check data availability
    const availability = useMemo(() => {
        const outputs = results?.outputs || {};
        return {
            load_profile: (loadProfile?.loads?.length > 0) || (outputs.Site?.load_kw?.length > 0),
            system_dispatch: (outputs.PV?.electric_to_load_series_kw?.length > 0) ||
                (outputs.ElectricStorage?.soc_series_fraction?.length > 0) ||
                (loadProfile?.loads?.length > 0),
            utility_bills: true, // Can always generate from available data
            cash_flows: true, // Can always generate from available data
            tariff: !!scenarioForm?.selectedTariffData || !!results?.inputs?.ElectricTariff,
            der_config: true,
            raw_results: !!results,
        };
    }, [results, loadProfile, scenarioForm]);

    return (
        <Box>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Export Scenario Data
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Download data files for use in external financial models
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" onClick={selectAll}>Select All</Button>
                            <Button size="small" onClick={selectNone}>Clear</Button>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        {EXPORT_CATEGORIES.map(category => {
                            const Icon = category.icon;
                            const isAvailable = availability[category.id];
                            const isSelected = selected.has(category.id);

                            return (
                                <Grid item xs={12} sm={6} key={category.id}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: isSelected ? 'primary.main' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                                            backgroundColor: isSelected ? (isDark ? 'rgba(255,200,100,0.05)' : 'rgba(25,118,210,0.05)') : 'transparent',
                                            opacity: isAvailable ? 1 : 0.5,
                                            cursor: isAvailable ? 'pointer' : 'not-allowed',
                                            transition: 'all 0.2s',
                                            '&:hover': isAvailable ? {
                                                borderColor: 'primary.main',
                                            } : {},
                                        }}
                                        onClick={() => isAvailable && toggleCategory(category.id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <Checkbox
                                                checked={isSelected}
                                                disabled={!isAvailable}
                                                onChange={() => toggleCategory(category.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                size="small"
                                            />
                                            <Icon sx={{ color: isSelected ? 'primary.main' : 'text.secondary', mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {category.label}
                                                    </Typography>
                                                    <Chip
                                                        label={category.format}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            fontSize: '0.65rem',
                                                            backgroundColor: category.format === 'CSV' ? 'success.main' : 'info.main',
                                                            color: 'white',
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {category.description}
                                                </Typography>
                                                {!isAvailable && (
                                                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                                        No data available
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="text"
                                                disabled={!isAvailable}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadSingle(category.id);
                                                }}
                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                            >
                                                <FileDownloadIcon fontSize="small" />
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleDownloadSelected}
                            disabled={selected.size === 0 || exporting}
                            sx={{
                                px: 4,
                                backgroundColor: 'primary.main',
                                '&:hover': { backgroundColor: 'primary.dark' },
                            }}
                        >
                            Download Selected ({selected.size})
                        </Button>
                    </Box>

                    {selected.size > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">
                                Files will download individually. For large datasets, downloads may take a moment.
                            </Typography>
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
